import { useMemo, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { useFormikContext } from 'formik';
import { useDrag, useDrop } from 'react-dnd';
import { IconButton, Popover, PopoverButton, StyledPopoverPanel, PopoverItem } from '@crazyegginc/hatch';

import { usePrevious, usePermissions, useOutsideClickNotify, useSelectedSite } from '/src/hooks';
import { useFunnelEditorContext } from '../../funnel-editor-context';
import { MAX_STEPS, STEP_TYPES, ENTER_SITE } from '../../constants';
import { SearchableGoalSelector } from '/src/components/SearchableGoalSelector';
import { StepButton } from './StepButton';
import { FEATURES } from '/src/features/_global/constants';

import { ReactComponent as MoveIcon } from '@crazyegginc/hatch/dist/images/icon-move-dots.svg';
import { ReactComponent as CrossIcon } from '@crazyegginc/hatch/dist/images/icon-cross.svg';
import { ReactComponent as MenuIcon } from '@crazyegginc/hatch/dist/images/icon-menu-dots.svg';
import { ReactComponent as GoalIcon } from '@crazyegginc/hatch/dist/images/icon-goal-filled.svg';
import { ReactComponent as ExternalIcon } from '@crazyegginc/hatch/dist/images/icon-open-url-outline.svg';

export function FunnelSteps({ goals, actions, stepsData }) {
  const { addStepActive, activateAddStep } = useFunnelEditorContext();
  const { values, setFieldValue } = useFormikContext();
  const { selectedSite } = useSelectedSite();
  const permissions = usePermissions();
  const canEditFunnel = permissions.can('edit', FEATURES.FUNNELS).allowed;

  const usedGoalIds = useMemo(() => stepsData.filter((s) => s.type === STEP_TYPES.GOAL).map((s) => s.id), [stepsData]);
  const canAddStep =
    canEditFunnel && stepsData.length < MAX_STEPS && !addStepActive && usedGoalIds.length < goals.length;

  const shouldRenameFunnel = useMemo(() => {
    if (stepsData.length === 0 && values.name === '') return true;
    if (stepsData.length === 1 && values.name === stepsData.at(0).name) return true;
    if (stepsData.length > 1 && values.name === `${stepsData.at(0).name} to ${stepsData.at(-1).name}`) return true;
    return false;
  }, [values.name, stepsData]);
  const preShouldRenameFunnel = usePrevious(shouldRenameFunnel);

  useEffect(() => {
    if (preShouldRenameFunnel && canEditFunnel) {
      let newName;
      if (stepsData.length === 0) newName = '';
      if (stepsData.length === 1) newName = stepsData.at(0).name;
      if (stepsData.length > 1) newName = `${stepsData.at(0).name} to ${stepsData.at(-1).name}`;
      setFieldValue('name', newName);
    }
  }, [preShouldRenameFunnel, stepsData, setFieldValue, canEditFunnel]);

  return (
    <>
      {stepsData.map((step, i) => (
        <Step
          key={step.id ?? step.name}
          index={i}
          step={step}
          actions={actions}
          usedGoalIds={usedGoalIds.filter((id) => id !== step.id)}
        />
      ))}

      {canAddStep ? <StepButton text="Add step" onClick={activateAddStep} blueShadow={stepsData.length === 0} /> : null}
      {addStepActive ? (
        <StepPicker
          actions={actions}
          usedGoalIds={usedGoalIds}
          showEnterSiteStep={true}
          enterSiteDisabledReason={
            stepsData.length !== 0 ? 'NOT_STEP_ONE' : !selectedSite.webAnalyticsEnabled ? 'ENABLE_WEB_ANALYTICS' : null
          }
        />
      ) : null}
    </>
  );
}

function StepPicker({ actions, usedGoalIds, showEnterSiteStep, enterSiteDisabledReason, index }) {
  const { deactiveActiveSteps } = useFunnelEditorContext();

  const ref = useRef();
  useOutsideClickNotify(ref, deactiveActiveSteps);

  const { push, replace } = actions;
  const onGoalSelect = useCallback(
    (goal) => {
      if (goal) {
        const newStep = goal.id === ENTER_SITE ? { type: STEP_TYPES.VISIT } : { type: STEP_TYPES.GOAL, id: goal.id };
        if (index != null) {
          replace(index, newStep);
        } else {
          push(newStep);
        }
        deactiveActiveSteps();
      }
    },
    [push, replace, deactiveActiveSteps, index],
  );

  return (
    <div
      className={classNames('first:mt-0 mt-2.5 first:before:invisible relative', {
        "before:content-[''] before:w-0.5 before:h-2.5 before:absolute before:bottom-full before:left-7 before:block before:bg-mystic-500":
          index != null,
      })}
      ref={ref}
      data-testid="step-picker"
    >
      <SearchableGoalSelector
        onSelect={onGoalSelect}
        showNoGoalOption={false}
        filterIds={usedGoalIds}
        displayIfNoGoals="Please create Goals to be able to select one."
        comboboxOptionsClassName="!w-[512px]"
        showEnterSiteStep={showEnterSiteStep}
        enterSiteDisabledReason={enterSiteDisabledReason}
        openOnMount={true}
        placeholder="Search"
        showSelected={false}
        showSearchIcon={true}
        clearButton={true}
      />
    </div>
  );
}

function Step({ step, index, actions, usedGoalIds }) {
  const { activateChangeStep, stepPickerActiveIndex } = useFunnelEditorContext();
  const { selectedSite } = useSelectedSite();
  const permissions = usePermissions();
  const canEditFunnel = permissions.can('edit', FEATURES.FUNNELS).allowed;

  const canEditStep = canEditFunnel && step.type !== STEP_TYPES.VISIT;

  const ref = useRef(null);
  const dragRef = useRef(null);
  const [, drop] = useDrop({
    accept: 'funnelstep',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    canDrop: () => canEditStep,
    hover(item, monitor) {
      if (!ref.current || !monitor.canDrop()) {
        return;
      }
      const dragIndex = item.i;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      actions.move(dragIndex, hoverIndex);
      item.i = hoverIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'funnelstep',
    item: () => {
      return { id: `${step.type}-${step.id ?? index}`, i: index };
    },
    canDrag: () => canEditStep,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  preview(drop(ref));
  drag(dragRef);

  if (stepPickerActiveIndex === index) {
    return (
      <StepPicker
        actions={actions}
        usedGoalIds={usedGoalIds}
        showEnterSiteStep={true}
        enterSiteDisabledReason={
          index !== 0 ? 'NOT_STEP_ONE' : !selectedSite.webAnalyticsEnabled ? 'ENABLE_WEB_ANALYTICS' : null
        }
        index={index}
      />
    );
  }

  return (
    <div
      className={classNames(
        'h-10 w-full flex items-center rounded-[5px] pl-0.5 pr-3.75 relative group justify-between bg-white',
        'border border-mystic-500 shadow',
        'first:mt-0 mt-2.5 first:before:invisible',
        "before:content-[''] before:w-0.5 before:h-2.5 before:absolute before:bottom-full before:left-7 before:block before:bg-mystic-500",
        { 'opacity-0': isDragging },
      )}
      ref={ref}
      data-testid="step-card"
    >
      <div className="flex items-center min-w-0">
        <div
          className={classNames('invisible mr-0.5', {
            'group-hover:visible cursor-[grab] text-cadet-blue-500': canEditStep,
            'cursor-not-allowed text-cadet-blue-500/50': !canEditStep,
          })}
          ref={dragRef}
          data-testid="step-drag-handle"
        >
          <MoveIcon className="h-4 w-4 fill-current" />
        </div>
        <div
          className={classNames(
            'flex shrink-0 items-center justify-center w-5 h-5 mr-2.5 rounded',
            'text-2xs font-semibold text-white bg-cadet-blue-500',
          )}
        >
          {index + 1}
        </div>
        <span className="truncate text-body-1">{step.name}</span>
      </div>
      <div className="items-center space-x-1 hidden group-hover:flex">
        {canEditStep ? (
          <Popover className="relative">
            <>
              <PopoverButton
                as={IconButton}
                className="text-cadet-blue-500 hover:text-dodger-blue-500 flex-shrink-0"
                icon={<MenuIcon className="h-4 w-4 fill-current" />}
                label="step menu"
              />
              <StyledPopoverPanel align="left" className="!w-48" data-testid="step-menu-panel">
                {({ close }) => (
                  <>
                    <PopoverItem
                      onClick={() => {
                        activateChangeStep(index);
                        close();
                      }}
                      className="group/menu flex items-center"
                    >
                      <GoalIcon className="mr-2.5 h-3.5 w-3.5 fill-current text-dodger-blue-300 group-hover/menu:text-dodger-blue-500" />
                      Change Goal
                    </PopoverItem>
                    <PopoverItem
                      component={Link}
                      to={`/goals/${step.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        close();
                      }}
                      className="group/menu flex items-center"
                    >
                      <ExternalIcon className="mr-2.5 h-3.5 w-3.5 fill-current text-dodger-blue-300 group-hover/menu:text-dodger-blue-500" />
                      View Goal Details
                    </PopoverItem>
                  </>
                )}
              </StyledPopoverPanel>
            </>
          </Popover>
        ) : null}
        {canEditFunnel ? (
          <IconButton
            icon={<CrossIcon className="h-2.5 w-2.5 fill-current" />}
            className="flex-shrink-0 text-cadet-blue-500 hover:text-carnation-500"
            onClick={() => {
              actions.remove(index);
            }}
            label={`delete step ${index + 1}`}
          />
        ) : null}
      </div>
    </div>
  );
}
